import { Alert, AlertTitle, Loader } from '@myosh/odin-components';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnSettings } from '@/components/schedules/column-settings';
import {
  buildScheduleColumns,
  defaultColumnVisibility,
  hierarchyColumnId,
  LEAD_COLUMN_IDS,
} from '@/components/schedules/columns';
import { ModeToggle } from '@/components/schedules/mode-toggle';
import { ScheduleStats } from '@/components/schedules/schedule-stats';
import { SchedulesPagination } from '@/components/schedules/schedules-pagination';
import { SchedulesTable } from '@/components/schedules/schedules-table';
import { useAppliedFilters } from '@/hooks/use-applied-filters';
import { useColumnSettings } from '@/hooks/use-column-settings';
import { useHierarchyTypes } from '@/hooks/use-hierarchy-types';
import { useScheduleData } from '@/hooks/use-schedule-data';
import { useScheduleMode } from '@/hooks/use-schedule-mode';
import { useSchedulePage } from '@/hooks/use-schedule-page';
import { resolveSelectionDates } from '@/utils/filter-selection';
import { buildDateBuckets } from '@/utils/schedule-buckets';

const CenteredMessage = ({ children }: { children: ReactNode }) => (
  <div className="flex h-full items-center justify-center p-6 text-muted-foreground text-sm">{children}</div>
);

export const SchedulesView = () => {
  const { t } = useTranslation();
  const { applied } = useAppliedFilters();
  const [mode] = useScheduleMode();
  const [page, setPage] = useSchedulePage();
  const { hierarchyTypes } = useHierarchyTypes();
  const { settings } = useColumnSettings();
  const locale = navigator.language;

  const { from, to } = resolveSelectionDates(applied, mode, new Date());
  const schedule = useScheduleData({ selection: applied, dates: { from, to }, page });

  const labels = useMemo(
    () => ({ form: t(($) => $.form), description: t(($) => $.description), schedule: t(($) => $.schedule) }),
    [t]
  );
  const columns = useMemo(
    () =>
      buildScheduleColumns({ hierarchyTypes, buckets: buildDateBuckets({ from, to }, mode, locale), locale, labels }),
    [hierarchyTypes, from, to, mode, labels]
  );
  const settingsColumns = useMemo(
    () => [
      ...hierarchyTypes.map((type) => ({ id: hierarchyColumnId(type.id), label: type.label, defaultVisible: false })),
      ...LEAD_COLUMN_IDS.map((id) => ({ id, label: labels[id], defaultVisible: true })),
    ],
    [hierarchyTypes, labels]
  );
  const columnVisibility = useMemo(
    () => ({ ...defaultColumnVisibility(hierarchyTypes), ...settings.visibility }),
    [hierarchyTypes, settings.visibility]
  );

  const renderContent = () => {
    if (!schedule.hasForms) {
      return <CenteredMessage>{t(($) => $.formSelectedRequired)}</CenteredMessage>;
    }
    if (schedule.isError) {
      return (
        <div className="p-4">
          <Alert variant="destructive">
            <AlertTitle>{t(($) => $.errorLoadingData)}</AlertTitle>
          </Alert>
        </div>
      );
    }
    if (schedule.rows.length === 0) {
      return schedule.isFetching ? (
        <CenteredMessage>
          <Loader size="sm" title={t(($) => $.loadingData)} />
        </CenteredMessage>
      ) : (
        <CenteredMessage>{t(($) => $.noRecordsFound)}</CenteredMessage>
      );
    }
    return (
      <SchedulesTable
        label={t(($) => $.scheduleViewer)}
        columns={columns}
        rows={schedule.rows}
        columnVisibility={columnVisibility}
        pinnedColumnIds={settings.pinned}
        isFetching={schedule.isFetching}
      />
    );
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {schedule.hashKey && <ScheduleStats hashKey={schedule.hashKey} />}
      <div className="flex items-center justify-end gap-2 border-b p-2">
        <ModeToggle />
        <ColumnSettings columns={settingsColumns} />
      </div>
      <div className="min-h-0 flex-1 overflow-auto">{renderContent()}</div>
      {schedule.hasForms && schedule.totalSize > 0 && (
        <SchedulesPagination
          page={page}
          availablePages={schedule.availablePages}
          onPageChange={(next) => void setPage(next)}
        />
      )}
    </div>
  );
};
